'use client';

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { format } from 'date-fns';
import { theme } from '@/config/theme';
import { SectionHeader } from '@/components/ui-components/SectionHeader';
import { TimelineScrollbarChevronIcon } from '@/components/icons/TimelineScrollbarChevronIcon';
import { i18n, replacePlaceholders } from '@/config/i18n';
import type {
  AnnotationHorizontalAnchor,
  AnnotationPlacement,
  AnnotationSide,
  BandKind,
  BandSegment,
  TimelineEventKind,
  TimelineHoverState,
  TimelineSectionAnnotationCopy,
  TimelineSectionEvent,
  TimelineSectionEventLabels,
  TimelineSectionProps,
} from '@/types/timelineSection';

const SLOT_MS = 5 * 60 * 1000;
/** Horizontal distance per 5-minute tick on the belt (same px spacing as prior 15-minute grid). */
const SLOT_WIDTH_PX = 120;
const LABEL_WIDTH_PX = 50;
const LABEL_HEIGHT_PX = 16;
/** Extra height for the timeline pan (belt + detail) vs the original 208px pan. */
const TIMELINE_PAN_EXTRA_HEIGHT_PX = 80;
/** Scrollbar rail: fixed overall height; inner strip fills the remainder after vertical padding. */
const SCROLLBAR_RAIL_PADDING_TOP_PX = 1;
const SCROLLBAR_RAIL_PADDING_RIGHT_PX = 4;
const SCROLLBAR_RAIL_PADDING_BOTTOM_PX = 1;
const SCROLLBAR_RAIL_PADDING_LEFT_PX = 4;
const TIMELINE_SCROLLBAR_ROW_PX = 16;
const SCROLLBAR_RAIL_INNER_HEIGHT_PX =
  TIMELINE_SCROLLBAR_ROW_PX - SCROLLBAR_RAIL_PADDING_TOP_PX - SCROLLBAR_RAIL_PADDING_BOTTOM_PX;
/** Prior pan was 208px; pan grows by {@link TIMELINE_PAN_EXTRA_HEIGHT_PX}. */
const TIMELINE_PAN_HEIGHT_PX = 208 + TIMELINE_PAN_EXTRA_HEIGHT_PX;
/** Total vertical stack: pan + scrollbar rail (used for flex sizing). */
const TIMELINE_CONTENT_HEIGHT_PX = TIMELINE_PAN_HEIGHT_PX + TIMELINE_SCROLLBAR_ROW_PX;
const BELT_HEIGHT_PX = 24;
const DETAIL_HEIGHT_PX = TIMELINE_PAN_HEIGHT_PX - BELT_HEIGHT_PX;
const SCROLL_THUMB_MAX_WIDTH_PX = 73;
const SCROLL_THUMB_HEIGHT_PX = 6;
const SCROLL_THUMB_MIN_WIDTH_PX = 24;
/** Vertical gap between the event connector dot and the annotation text (spec). */
const ANNOTATION_DOT_TO_TEXT_GAP_PX = 2;
const BAR_HEIGHT_PX = 12;
const BAR_RADIUS_PX = 6;
/** Visual gap between adjacent status band segments (spec: exactly 1px). */
const SEGMENT_GAP_PX = 1;

/** Single green for all healthy / success / service-restored timeline visuals. */
const TIMELINE_SUCCESS_GREEN = '#6A9A23';

/** Status band vertically centered in the detail pan. */
const BAR_ROW_TOP_PX = Math.floor((DETAIL_HEIGHT_PX - BAR_HEIGHT_PX) / 2);
const BAND_TOP_PX = BAR_ROW_TOP_PX;
const BAND_BOTTOM_PX = BAR_ROW_TOP_PX + BAR_HEIGHT_PX;
const ABOVE_REGION_HEIGHT_PX = BAND_TOP_PX;
const BELOW_REGION_HEIGHT_PX = DETAIL_HEIGHT_PX - BAND_BOTTOM_PX;
/** Tier 0 = full connector to section edge; higher tiers lower labels on the above side. */
const MAX_CONNECTOR_TIER = 4;
/** Minimum horizontal and vertical separation between annotation labels. */
const ANNOTATION_LABEL_MIN_GAP_PX = 10;
/** Estimated annotation block height (time line + gap + detail line) for overlap math. */
const ANNOTATION_LABEL_BLOCK_HEIGHT_PX = LABEL_HEIGHT_PX + ANNOTATION_DOT_TO_TEXT_GAP_PX + LABEL_HEIGHT_PX;
/** Keep annotation labels slightly inside the track edges before flipping anchor side. */
const ANNOTATION_TRACK_EDGE_INSET_PX = 4;
const ANNOTATION_EDGE_INSET_PX = 4;

/** Smooth arrow / track jumps unless the user prefers reduced motion. */
function scrollBehaviorForProgrammaticScroll(): ScrollBehavior {
  if (typeof window === 'undefined') return 'auto';
  return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth';
}

const panels = theme.colors.pageScrapper.panels;

const TimelineSectionOuter = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: clip;
  overflow-y: visible;
  overscroll-behavior-x: contain;
`;

/** White surface: title + scrollable pan only (no scrollbar inside this card). */
const Root = styled.section`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0;
  min-height: 0px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: clip;
  overflow-y: visible;
  overscroll-behavior-x: contain;
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.md} ${theme.borderRadius.md} 0 0;
  padding: ${theme.spacing.md};

  /* Flex column: prevent children (e.g. header) from forcing min-content width wider than viewport */
  & > * {
    min-width: 0;
  }
`;

/** Keeps title/subtitle from widening the card when the track is wide. */
const SectionHeaderSlot = styled.div`
  min-width: 0;
  width: 100%;
  margin-bottom: ${theme.spacing.lg};
`;

const TimelinePan = styled.div`
  box-sizing: border-box;
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: ${TIMELINE_PAN_HEIGHT_PX}px;
  flex: 0 0 ${TIMELINE_PAN_HEIGHT_PX}px;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  /* Allow vertical wheel to chain to the page; overscroll-behavior-y: none blocked page scroll over the pan. */
  overscroll-behavior-y: auto;
  contain: inline-size;
  isolation: isolate;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

/** Sits below {@link Root} (not inside the white card), flush with zero gap. */
const ScrollbarRail = styled.div`
  box-sizing: border-box;
  flex: 0 0 ${TIMELINE_SCROLLBAR_ROW_PX}px;
  width: 100%;
  height: ${TIMELINE_SCROLLBAR_ROW_PX}px;
  min-height: ${TIMELINE_SCROLLBAR_ROW_PX}px;
  margin-top: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background: ${panels.timelineScrollbarRail};
  border-right: 1px solid ${panels.cardBorder};
  border-bottom: 1px solid ${panels.cardBorder};
  border-left: 1px solid ${panels.cardBorder};


  border-radius: 0px 0px ${theme.borderRadius.md} ${theme.borderRadius.md} ;
`;

const ScrollArrowButton = styled.button`
  box-sizing: border-box;
  flex: 0 0 20px;
  width: 20px;
  height: ${SCROLLBAR_RAIL_INNER_HEIGHT_PX}px;
  min-height: ${SCROLLBAR_RAIL_INNER_HEIGHT_PX}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.accent};
    outline-offset: 2px;
  }
`;

const ThumbTrack = styled.div`
  box-sizing: border-box;
  flex: 1 1 0;
  min-width: 0;
  width: 0;
  height: ${SCROLLBAR_RAIL_INNER_HEIGHT_PX}px;
  min-height: ${SCROLLBAR_RAIL_INNER_HEIGHT_PX}px;
  position: relative;
  align-self: center;
`;

const ScrollThumb = styled.div<{ $leftPx: number; $widthPx: number }>`
  position: absolute;
  top: 50%;
  left: 0;
  width: ${({ $widthPx }) => $widthPx}px;
  height: ${SCROLL_THUMB_HEIGHT_PX}px;
  border-radius: ${theme.borderRadius.lg};
  background: ${panels.subtitleMuted};
  cursor: grab;
  touch-action: none;
  /* translate3d keeps thumb movement on the compositor (smoother than updating the left offset). */
  transform: translate3d(${({ $leftPx }) => $leftPx}px, -50%, 0);

  &:active {
    cursor: grabbing;
  }
`;

const Track = styled.div<{ $widthPx: number }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  position: relative;
  width: ${({ $widthPx }) => $widthPx}px;
  min-width: ${({ $widthPx }) => $widthPx}px;
  max-width: ${({ $widthPx }) => $widthPx}px;
  height: 100%;
  /* Abs-positioned labels/connectors must not widen the page — clip to the track width */
  overflow: hidden;
`;

const Belt = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  flex: 0 0 ${BELT_HEIGHT_PX}px;
  height: ${BELT_HEIGHT_PX}px;
  min-height: ${BELT_HEIGHT_PX}px;
  width: 100%;
  min-width: 100%;
`;

const TickCell = styled.div`
  box-sizing: border-box;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
`;

const TickLabel = styled.span`
  box-sizing: border-box;
  width: ${LABEL_WIDTH_PX}px;
  height: ${LABEL_HEIGHT_PX}px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  font-family: ${theme.fonts.body};
  font-weight: ${theme.fontWeights.normal};
  font-size: 12px;
  line-height: 16px;
  color: ${panels.nameUppercase};
  white-space: nowrap;
`;

const Detail = styled.div`
  box-sizing: border-box;
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  height: ${DETAIL_HEIGHT_PX}px;
  overflow: hidden;
`;

/** Snap vertical lines to whole pixels — avoids subpixel blur that looks like width changes. */
function snapTimelineLineLeftPx(leftPx: number): number {
  return Math.round(leftPx);
}

const verticalDashedLineFill = (color: string) => css`
  background-image: repeating-linear-gradient(
    to bottom,
    ${color} 0,
    ${color} 2px,
    transparent 2px,
    transparent 4px
  );
  background-repeat: repeat-y;
  background-size: 1px 4px;
`;

const GuidesLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
`;

/** Background guides: 1px wide, 2px dash / 2px gap (matches 2,2 dash spec). */
const GuideLine = styled.div<{ $leftPx: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $leftPx }) => $leftPx}px;
  width: 1px;
  ${verticalDashedLineFill(panels.scrollbarThumb)}
`;

const ConnectorsLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
`;

/** Narrow hit target on the dashed connector column only. */
const EventConnectorHitTarget = styled.div<{
  $leftPx: number;
  $topPx: number;
  $heightPx: number;
}>`
  position: absolute;
  top: ${({ $topPx }) => $topPx}px;
  left: ${({ $leftPx }) => $leftPx}px;
  width: 10px;
  height: ${({ $heightPx }) => $heightPx}px;
  transform: translateX(-50%);
  pointer-events: auto;
  z-index: 3;
  background: transparent;
`;

/** Hit target sized to the annotation text block only. */
const EventAnnotationHitTarget = styled.div<{
  $leftPx: number;
  $topPx: number;
  $widthPx: number;
  $heightPx: number;
  $anchor: AnnotationHorizontalAnchor;
}>`
  position: absolute;
  top: ${({ $topPx }) => $topPx}px;
  left: ${({ $leftPx }) => $leftPx}px;
  width: ${({ $widthPx }) => $widthPx}px;
  height: ${({ $heightPx }) => $heightPx}px;
  transform: ${({ $anchor }) => {
    if ($anchor === 'end') return 'translateX(-100%)';
    if ($anchor === 'start') return 'translateX(0)';
    return 'translateX(-50%)';
  }};
  pointer-events: auto;
  z-index: 3;
  background: transparent;
`;

/** Grace period (ms) when moving between connector and label of the same event. */
const EVENT_HOVER_LEAVE_DELAY_MS = 50;
/** Opacity for non-highlighted connectors/labels while another event or band is hovered. */
const TIMELINE_DIMMED_OPACITY = 0.15;

/** Per-event column: dashed connector between the band and the annotation label. */
const EventConnectorColumn = styled.div<{
  $leftPx: number;
  $topPx: number;
  $heightPx: number;
  $dimmed: boolean;
  $highlighted: boolean;
}>`
  position: absolute;
  left: ${({ $leftPx }) => $leftPx}px;
  top: ${({ $topPx }) => $topPx}px;
  height: ${({ $heightPx }) => $heightPx}px;
  width: 1px;
  pointer-events: none;
  cursor: default;
  opacity: ${({ $dimmed }) => ($dimmed ? TIMELINE_DIMMED_OPACITY : 1)};
  transition: opacity 0.15s ease;
  z-index: ${({ $highlighted }) => ($highlighted ? 4 : 2)};
`;

/** Line below label dot, down to the band. */
const EventConnectorLineAbove = styled.div<{ $lineColor: string }>`
  position: absolute;
  top: 6px;
  left: 0;
  width: 1px;
  bottom: 0;
  min-height: 0;
  ${({ $lineColor }) => verticalDashedLineFill($lineColor)}
`;

/** Line from band up to the label dot. */
const EventConnectorLineBelow = styled.div<{ $lineColor: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  bottom: 6px;
  min-height: 0;
  ${({ $lineColor }) => verticalDashedLineFill($lineColor)}
`;

const EventConnectorDot = styled.span<{ $color: string; $side: AnnotationSide }>`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  ${({ $side }) => ($side === 'above' ? 'top: 0;' : 'bottom: 0;')}
`;

const BarsLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 99;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

const DurationBar = styled.div<{ $leftPx: number; $widthPx: number; $bg: string; $dimmed?: boolean }>`
  position: absolute;
  top: ${BAR_ROW_TOP_PX}px;
  left: ${({ $leftPx }) => $leftPx}px;
  width: ${({ $widthPx }) => $widthPx}px;
  height: ${BAR_HEIGHT_PX}px;
  border-radius: ${BAR_RADIUS_PX}px;
  box-sizing: border-box;
  background: ${({ $bg }) => $bg};
  z-index: 1;
  pointer-events: none;
  opacity: ${({ $dimmed }) => ($dimmed ? TIMELINE_DIMMED_OPACITY : 1)};
  transition: opacity 0.15s ease;
`;

/** Invisible hit target over each status band for kind-based label highlighting. */
const DurationBarHitTarget = styled.div<{ $leftPx: number; $widthPx: number }>`
  position: absolute;
  top: ${BAR_ROW_TOP_PX}px;
  left: ${({ $leftPx }) => $leftPx}px;
  width: ${({ $widthPx }) => $widthPx}px;
  height: ${BAR_HEIGHT_PX}px;
  border-radius: ${BAR_RADIUS_PX}px;
  box-sizing: border-box;
  z-index: 2;
  background: transparent;
  cursor: default;
`;

const AnnotationsLayer = styled.div`
  box-sizing: border-box;
  position: absolute;
  inset: 0;
  z-index: 100;
  pointer-events: none;
`;

const AnnotationBlock = styled.div<{
  $leftPx: number;
  $topPx: number;
  $anchor: AnnotationHorizontalAnchor;
  $dimmed: boolean;
  $highlighted: boolean;
}>`
  position: absolute;
  top: ${({ $topPx }) => $topPx}px;
  left: ${({ $leftPx }) => $leftPx}px;
  transform: ${({ $anchor }) => {
    if ($anchor === 'end') return 'translateX(-100%)';
    if ($anchor === 'start') return 'translateX(0)';
    return 'translateX(-50%)';
  }};
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  max-width: 160px;
  min-width: 0;
  pointer-events: none;
  cursor: default;
  opacity: ${({ $dimmed }) => ($dimmed ? TIMELINE_DIMMED_OPACITY : 1)};
  transition: opacity 0.15s ease;
  z-index: ${({ $highlighted }) => ($highlighted ? 102 : 100)};
`;

const AnnotationText = styled.div<{ $anchor: AnnotationHorizontalAnchor }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  align-items: ${({ $anchor }) =>
    $anchor === 'end' ? 'flex-end' : $anchor === 'start' ? 'flex-start' : 'center'};
  text-align: ${({ $anchor }) =>
    $anchor === 'end' ? 'right' : $anchor === 'start' ? 'left' : 'center'};
`;

const AnnotationTimeLine = styled.span`
  font-family: ${theme.fonts.body};
  font-weight: ${theme.fontWeights.normal};
  font-size: 12px;
  line-height: 16px;
  color: ${panels.timelineAnnotationTime};
  white-space: nowrap;
`;

const AnnotationDetailLine = styled.span<{ $color: string }>`
  font-family: ${theme.fonts.body};
  font-weight: ${theme.fontWeights.bold};
  font-size: 12px;
  line-height: 16px;
  color: ${({ $color }) => $color};
  white-space: nowrap;
`;

function floorToSlotBoundary(d: Date): Date {
  const t = d.getTime();
  return new Date(Math.floor(t / SLOT_MS) * SLOT_MS);
}

function buildTicks(windowStart: Date, windowEnd: Date): Date[] {
  if (!(windowEnd.getTime() > windowStart.getTime())) {
    return [];
  }
  const first = floorToSlotBoundary(windowStart);
  const ticks: Date[] = [];
  let t = first.getTime();
  const endMs = windowEnd.getTime();
  const guard = 10000;
  let n = 0;
  while (t <= endMs && n < guard) {
    ticks.push(new Date(t));
    t += SLOT_MS;
    n += 1;
  }
  return ticks;
}

function timeToPx(tMs: number, anchorMs: number, slotWidthPx: number): number {
  return ((tMs - anchorMs) / SLOT_MS) * slotWidthPx;
}

function eventSeverity(kind: TimelineEventKind): number {
  switch (kind) {
    case 'down_detected':
      return 3;
    case 'high_latency':
      return 2;
    case 'service_restored':
      return 1;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

function kindAtMidpoint(midMs: number, events: TimelineSectionEvent[]): BandKind {
  let best: { sev: number; kind: TimelineEventKind } | null = null;
  for (const e of events) {
    const s = e.start.getTime();
    const en = e.end.getTime();
    if (midMs >= s && midMs < en) {
      const sev = eventSeverity(e.kind);
      if (!best || sev > best.sev) {
        best = { sev, kind: e.kind };
      }
    }
  }
  if (!best) return 'up';
  if (best.kind === 'service_restored') return 'service_restored';
  return best.kind;
}

/** Event label kinds associated with a single status band segment. */
function eventKindMatchesBandSegment(eventKind: TimelineEventKind, bandKind: BandKind): boolean {
  if (bandKind === 'up') return eventKind === 'service_restored';
  if (bandKind === 'service_restored') return eventKind === 'service_restored';
  return eventKind === bandKind;
}

/** Maps each timeline event to the band segment it most closely belongs to. */
function resolveEventBandSegmentId(
  ev: TimelineSectionEvent,
  segments: BandSegment[],
): string | undefined {
  const evStart = ev.start.getTime();
  const evEnd = ev.end.getTime();
  const anchorMs = annotationAnchorMs(ev);
  let bestSegId: string | undefined;
  let bestScore = -1;

  for (const seg of segments) {
    if (!eventKindMatchesBandSegment(ev.kind, seg.kind)) continue;

    const overlapStart = Math.max(evStart, seg.startMs);
    const overlapEnd = Math.min(evEnd, seg.endMs);
    const overlap = Math.max(0, overlapEnd - overlapStart);
    const anchorInside = anchorMs >= seg.startMs && anchorMs < seg.endMs;

    if (overlap <= 0 && !anchorInside) continue;

    const score = overlap + (anchorInside ? 1e15 : 0);
    if (score > bestScore) {
      bestScore = score;
      bestSegId = seg.id;
    }
  }

  return bestSegId;
}

function isTimelineEventHighlighted(
  hover: TimelineHoverState,
  eventId: string,
  eventBandSegmentId: string | undefined,
): boolean {
  if (!hover) return false;
  if (hover.mode === 'event') return hover.eventId === eventId;
  return eventBandSegmentId != null && eventBandSegmentId === hover.segmentId;
}

function isTimelineEventDimmed(
  hover: TimelineHoverState,
  eventId: string,
  eventBandSegmentId: string | undefined,
): boolean {
  if (!hover) return false;
  return !isTimelineEventHighlighted(hover, eventId, eventBandSegmentId);
}

function isBandSegmentDimmed(
  hover: TimelineHoverState,
  segmentId: string,
  eventBandSegmentIdByEventId: Map<string, string>,
): boolean {
  if (!hover) return false;
  const highlightedSegmentId =
    hover.mode === 'band'
      ? hover.segmentId
      : eventBandSegmentIdByEventId.get(hover.eventId);
  if (!highlightedSegmentId) return false;
  return segmentId !== highlightedSegmentId;
}

function segmentBarColors(kind: BandKind): {
  bar: string;
  connectorColor: string;
  detailColor: string;
} {
  switch (kind) {
    case 'up':
      return {
        bar: TIMELINE_SUCCESS_GREEN,
        connectorColor: TIMELINE_SUCCESS_GREEN,
        detailColor: TIMELINE_SUCCESS_GREEN,
      };
    case 'service_restored':
      return {
        bar: TIMELINE_SUCCESS_GREEN,
        connectorColor: TIMELINE_SUCCESS_GREEN,
        detailColor: TIMELINE_SUCCESS_GREEN,
      };
    case 'down_detected':
      return {
        bar: panels.timelineDownConnectorLine,
        connectorColor: panels.timelineDownConnectorLine,
        detailColor: theme.colors.status.errorDark,
      };
    case 'high_latency':
      return {
        bar: panels.timelineHighLatencyConnectorLine,
        connectorColor: panels.timelineHighLatencyConnectorLine,
        detailColor: panels.timelineHighLatencyConnector,
      };
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

/** Merge adjacent band spans that paint the same bar color (e.g. `up` + `service_restored` green). */
function barPaintKey(kind: BandKind): string {
  const { bar } = segmentBarColors(kind);
  return bar;
}

function mergeAdjacentSameBarSegments(segments: BandSegment[]): BandSegment[] {
  if (segments.length === 0) return segments;
  const out: BandSegment[] = [];
  for (const seg of segments) {
    const prev = out[out.length - 1];
    if (prev && barPaintKey(prev.kind) === barPaintKey(seg.kind)) {
      prev.widthPx = seg.leftPx + seg.widthPx - prev.leftPx;
      prev.endMs = seg.endMs;
      if (barPaintKey(prev.kind) === barPaintKey('up')) {
        prev.kind = 'up';
      }
    } else {
      out.push({ ...seg });
    }
  }
  return out;
}

function labelForKind(kind: TimelineEventKind, labels: TimelineSectionEventLabels): string {
  switch (kind) {
    case 'down_detected':
      return labels.downDetected;
    case 'high_latency':
      return labels.highLatency;
    case 'service_restored':
      return labels.serviceRestored;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

/** Recovery marker sits at the **start** of the green band (when service came back), not the segment end. */
function annotationAnchorMs(ev: TimelineSectionEvent): number {
  return ev.start.getTime();
}

function formatDuration(
  start: Date,
  end: Date,
  copy: TimelineSectionAnnotationCopy,
): string {
  const ms = Math.max(0, end.getTime() - start.getTime());
  const totalMins = Math.max(1, Math.round(ms / 60000));
  if (totalMins >= 60) {
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    if (m === 0) {
      return replacePlaceholders(copy.durationHoursOnly, { hours: String(h) });
    }
    return replacePlaceholders(copy.durationHoursMinutes, { hours: String(h), minutes: String(m) });
  }
  return replacePlaceholders(copy.durationMinutes, { minutes: String(totalMins) });
}

/** Conservative width estimate for horizontal overlap detection (labels use max-width 160px). */
function estimateAnnotationLabelWidthPx(p: { timeLine: string; detailLine: string }): number {
  const byTime = p.timeLine.length * 7.2;
  const byDetail = Math.min(160, p.detailLine.length * 6.2);
  return Math.min(160, Math.max(48, Math.max(byTime, byDetail)));
}

/** Flip label to the left of the connector when the centered box would clip on the right edge. */
function computeAnnotationHorizontalAnchor(
  connectorLeftPx: number,
  labelWidthPx: number,
  trackWidthPx: number,
): AnnotationHorizontalAnchor {
  const half = labelWidthPx / 2;
  const maxRight = trackWidthPx - ANNOTATION_TRACK_EDGE_INSET_PX;
  const minLeft = ANNOTATION_TRACK_EDGE_INSET_PX;
  const rightOverflow = connectorLeftPx + half > maxRight;
  const leftOverflow = connectorLeftPx - half < minLeft;

  if (rightOverflow && !leftOverflow) {
    return 'end';
  }
  if (leftOverflow && !rightOverflow) {
    return 'start';
  }
  if (rightOverflow && leftOverflow) {
    const spaceRight = trackWidthPx - connectorLeftPx;
    const spaceLeft = connectorLeftPx;
    return spaceRight >= spaceLeft ? 'start' : 'end';
  }
  return 'center';
}

function annotationLabelCenterX(
  connectorLeftPx: number,
  width: number,
  anchor: AnnotationHorizontalAnchor,
): number {
  if (anchor === 'end') return connectorLeftPx - width / 2;
  if (anchor === 'start') return connectorLeftPx + width / 2;
  return connectorLeftPx;
}

/** Y coordinate (px from detail top) where the connector line ends near the label. */
function connectorLineEndY(side: AnnotationSide, tier: number): number {
  if (side === 'above') {
    if (tier <= 0) {
      return ANNOTATION_EDGE_INSET_PX + ANNOTATION_LABEL_BLOCK_HEIGHT_PX;
    }
    const step = Math.min(tier, MAX_CONNECTOR_TIER - 1);
    const fraction = 0.28 + step * 0.18;
    return BAND_TOP_PX - ABOVE_REGION_HEIGHT_PX * Math.min(0.92, fraction);
  }
  if (tier <= 0) {
    return DETAIL_HEIGHT_PX - ANNOTATION_EDGE_INSET_PX - ANNOTATION_LABEL_BLOCK_HEIGHT_PX;
  }
  const step = Math.min(tier, MAX_CONNECTOR_TIER - 1);
  const fraction = 0.28 + step * 0.18;
  return BAND_BOTTOM_PX + BELOW_REGION_HEIGHT_PX * Math.min(0.92, fraction);
}

/** Connector column box within the detail pan (absolute coordinates). */
function connectorColumnBox(
  side: AnnotationSide,
  tier: number,
): { topPx: number; heightPx: number } {
  const lineEndY = connectorLineEndY(side, tier);
  if (side === 'above') {
    return {
      topPx: lineEndY,
      heightPx: Math.max(0, BAND_TOP_PX - lineEndY),
    };
  }
  return {
    topPx: BAND_BOTTOM_PX,
    heightPx: Math.max(0, lineEndY - BAND_BOTTOM_PX),
  };
}

/** Top edge of annotation block (px from detail top). */
function annotationLabelTopPx(side: AnnotationSide, tier: number): number {
  const lineEndY = connectorLineEndY(side, tier);
  if (side === 'above') {
    return Math.max(
      ANNOTATION_EDGE_INSET_PX,
      lineEndY - ANNOTATION_DOT_TO_TEXT_GAP_PX - ANNOTATION_LABEL_BLOCK_HEIGHT_PX,
    );
  }
  return Math.min(
    DETAIL_HEIGHT_PX - ANNOTATION_EDGE_INSET_PX - ANNOTATION_LABEL_BLOCK_HEIGHT_PX,
    lineEndY + ANNOTATION_DOT_TO_TEXT_GAP_PX,
  );
}

/** Alternate annotation placement: first event above the band, then below, and so on. */
function computeAnnotationSidesByOrder(
  layouts: { id: string; connectorLeftPx: number }[],
): Record<string, AnnotationSide> {
  const sorted = [...layouts].sort((a, b) => a.connectorLeftPx - b.connectorLeftPx);
  const out: Record<string, AnnotationSide> = {};
  sorted.forEach((layout, index) => {
    out[layout.id] = index % 2 === 0 ? 'above' : 'below';
  });
  return out;
}

function annotationLabelRectsOverlap(
  a: { centerX: number; width: number; top: number; height: number },
  b: { centerX: number; width: number; top: number; height: number },
  minGapPx: number = ANNOTATION_LABEL_MIN_GAP_PX,
): boolean {
  const ax0 = a.centerX - a.width / 2;
  const ax1 = a.centerX + a.width / 2;
  const bx0 = b.centerX - b.width / 2;
  const bx1 = b.centerX + b.width / 2;
  const horizontalOverlap = ax1 + minGapPx > bx0 && bx1 + minGapPx > ax0;
  if (!horizontalOverlap) return false;
  const ay1 = a.top + a.height;
  const by1 = b.top + b.height;
  return a.top < by1 + minGapPx && b.top < ay1 + minGapPx;
}

function buildAnnotationLabelRect(
  connectorLeftPx: number,
  width: number,
  anchor: AnnotationHorizontalAnchor,
  topPx: number,
): { centerX: number; width: number; top: number; height: number } {
  return {
    centerX: annotationLabelCenterX(connectorLeftPx, width, anchor),
    width,
    top: topPx,
    height: ANNOTATION_LABEL_BLOCK_HEIGHT_PX,
  };
}

function connectorColumnBoxFromLabelTop(
  side: AnnotationSide,
  labelTopPx: number,
): { topPx: number; heightPx: number } {
  const lineEndY =
    side === 'above'
      ? labelTopPx + ANNOTATION_LABEL_BLOCK_HEIGHT_PX + ANNOTATION_DOT_TO_TEXT_GAP_PX
      : labelTopPx - ANNOTATION_DOT_TO_TEXT_GAP_PX;
  if (side === 'above') {
    return {
      topPx: lineEndY,
      heightPx: Math.max(0, BAND_TOP_PX - lineEndY),
    };
  }
  return {
    topPx: BAND_BOTTOM_PX,
    heightPx: Math.max(0, lineEndY - BAND_BOTTOM_PX),
  };
}

const ABOVE_LABEL_MAX_TOP_PX =
  BAND_TOP_PX - ANNOTATION_DOT_TO_TEXT_GAP_PX - ANNOTATION_LABEL_BLOCK_HEIGHT_PX - 6;
const BELOW_LABEL_MIN_TOP_PX =
  BAND_BOTTOM_PX + ANNOTATION_DOT_TO_TEXT_GAP_PX + 6 + ANNOTATION_LABEL_BLOCK_HEIGHT_PX;

function findLabelTopOnSide(
  side: AnnotationSide,
  connectorLeftPx: number,
  width: number,
  anchor: AnnotationHorizontalAnchor,
  placed: { centerX: number; width: number; top: number; height: number }[],
): number | null {
  if (side === 'above') {
    let candidateTop = ANNOTATION_EDGE_INSET_PX;
    const step = ANNOTATION_LABEL_BLOCK_HEIGHT_PX + ANNOTATION_LABEL_MIN_GAP_PX;
    while (candidateTop <= ABOVE_LABEL_MAX_TOP_PX) {
      const rect = buildAnnotationLabelRect(connectorLeftPx, width, anchor, candidateTop);
      const conflict = placed.find((prev) => annotationLabelRectsOverlap(prev, rect));
      if (!conflict) return candidateTop;
      candidateTop = Math.max(candidateTop + 1, conflict.top + conflict.height + ANNOTATION_LABEL_MIN_GAP_PX);
    }
    return null;
  }

  let candidateTop =
    DETAIL_HEIGHT_PX - ANNOTATION_EDGE_INSET_PX - ANNOTATION_LABEL_BLOCK_HEIGHT_PX;
  while (candidateTop >= BELOW_LABEL_MIN_TOP_PX) {
    const rect = buildAnnotationLabelRect(connectorLeftPx, width, anchor, candidateTop);
    const conflict = placed.find((prev) => annotationLabelRectsOverlap(prev, rect));
    if (!conflict) return candidateTop;
    candidateTop = Math.min(
      candidateTop - 1,
      conflict.top - ANNOTATION_LABEL_BLOCK_HEIGHT_PX - ANNOTATION_LABEL_MIN_GAP_PX,
    );
  }
  return null;
}

function tryPlaceOnSide(
  side: AnnotationSide,
  connectorLeftPx: number,
  width: number,
  anchor: AnnotationHorizontalAnchor,
  placed: { centerX: number; width: number; top: number; height: number }[],
): AnnotationPlacement | null {
  const labelTopPx = findLabelTopOnSide(side, connectorLeftPx, width, anchor, placed);
  if (labelTopPx == null) return null;
  const columnBox = connectorColumnBoxFromLabelTop(side, labelTopPx);
  const connectorTier =
    side === 'above'
      ? Math.round(
          ((labelTopPx - ANNOTATION_EDGE_INSET_PX) /
            Math.max(1, ABOVE_LABEL_MAX_TOP_PX - ANNOTATION_EDGE_INSET_PX)) *
            (MAX_CONNECTOR_TIER - 1),
        )
      : Math.round(
          ((DETAIL_HEIGHT_PX -
            ANNOTATION_EDGE_INSET_PX -
            ANNOTATION_LABEL_BLOCK_HEIGHT_PX -
            labelTopPx) /
            Math.max(
              1,
              DETAIL_HEIGHT_PX -
                ANNOTATION_EDGE_INSET_PX -
                ANNOTATION_LABEL_BLOCK_HEIGHT_PX -
                BELOW_LABEL_MIN_TOP_PX,
            )) *
            (MAX_CONNECTOR_TIER - 1),
        );
  return {
    side,
    labelTopPx,
    connectorTier,
    connectorColumnTopPx: columnBox.topPx,
    connectorColumnHeightPx: columnBox.heightPx,
  };
}

function annotationOverlapLabelWidthPx(
  p: { id: string; timeLine: string; detailLine: string },
  widthById: Record<string, number>,
): number {
  const m = widthById[p.id];
  if (typeof m === 'number' && m > 0 && Number.isFinite(m)) {
    return m;
  }
  return estimateAnnotationLabelWidthPx(p);
}

function sameAnnotationWidthMap(a: Record<string, number>, b: Record<string, number>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}

/**
 * Places labels left-to-right with 10px gaps, lowering on the above side / raising on the below side.
 * When the preferred below side is full, tries the above side (and vice versa).
 */
function computeAnnotationPlacementsByOverlap(
  layouts: {
    id: string;
    connectorLeftPx: number;
    timeLine: string;
    detailLine: string;
    preferredSide: AnnotationSide;
  }[],
  widthById: Record<string, number>,
  trackWidthPx: number,
): Record<string, AnnotationPlacement> {
  const out: Record<string, AnnotationPlacement> = {};
  const placedBySide: Record<AnnotationSide, { centerX: number; width: number; top: number; height: number }[]> = {
    above: [],
    below: [],
  };

  const sorted = [...layouts].sort((a, b) => a.connectorLeftPx - b.connectorLeftPx);

  for (const curr of sorted) {
    const currW = annotationOverlapLabelWidthPx(curr, widthById);
    const currAnchor = computeAnnotationHorizontalAnchor(
      curr.connectorLeftPx,
      currW,
      trackWidthPx,
    );

    const oppositeSide: AnnotationSide = curr.preferredSide === 'above' ? 'below' : 'above';
    const sideOrder: AnnotationSide[] = [curr.preferredSide, oppositeSide];

    let placement: AnnotationPlacement | null = null;
    for (const side of sideOrder) {
      placement = tryPlaceOnSide(
        side,
        curr.connectorLeftPx,
        currW,
        currAnchor,
        placedBySide[side],
      );
      if (placement) break;
    }

    if (!placement) {
      const fallbackTop =
        curr.preferredSide === 'above'
          ? ABOVE_LABEL_MAX_TOP_PX
          : DETAIL_HEIGHT_PX - ANNOTATION_EDGE_INSET_PX - ANNOTATION_LABEL_BLOCK_HEIGHT_PX;
      const columnBox = connectorColumnBoxFromLabelTop(curr.preferredSide, fallbackTop);
      placement = {
        side: curr.preferredSide,
        labelTopPx: fallbackTop,
        connectorTier: MAX_CONNECTOR_TIER - 1,
        connectorColumnTopPx: columnBox.topPx,
        connectorColumnHeightPx: columnBox.heightPx,
      };
    }

    out[curr.id] = placement;
    placedBySide[placement.side].push(
      buildAnnotationLabelRect(
        curr.connectorLeftPx,
        currW,
        currAnchor,
        placement.labelTopPx,
      ),
    );
  }

  return out;
}

function buildBandSegments(
  trackStartMs: number,
  trackEndMs: number,
  events: TimelineSectionEvent[],
  anchorMs: number,
  trackWidthPx: number,
  slotWidthPx: number,
): BandSegment[] {
  const bounds = new Set<number>([trackStartMs, trackEndMs]);
  for (const e of events) {
    const s = Math.max(trackStartMs, e.start.getTime());
    const en = Math.min(trackEndMs, e.end.getTime());
    if (en > s) {
      bounds.add(s);
      bounds.add(en);
    }
  }
  const sorted = [...bounds].sort((a, b) => a - b);
  const lastIdx = sorted.length - 1;
  const segments: BandSegment[] = [];
  const g = SEGMENT_GAP_PX;

  for (let i = 0; i < lastIdx; i += 1) {
    const t0 = sorted[i]!;
    const t1 = sorted[i + 1]!;
    if (!(t1 > t0)) continue;
    const mid = (t0 + t1) / 2;
    const kind = kindAtMidpoint(mid, events);
    const rawLeft = timeToPx(t0, anchorMs, slotWidthPx);
    const rawRight = timeToPx(t1, anchorMs, slotWidthPx);
    const insetLeft = i === 0 ? 0 : g / 2;
    const insetRight = i === lastIdx - 1 ? 0 : g / 2;
    const leftPx = Math.max(0, rawLeft + insetLeft);
    const rightPx = Math.min(trackWidthPx, rawRight - insetRight);
    const widthPx = Math.max(2, rightPx - leftPx);
    segments.push({ id: `band-${i}-${t0}`, leftPx, widthPx, kind, startMs: t0, endMs: t1 });
  }
  return segments;
}

function buildBandSegmentsMerged(
  trackStartMs: number,
  trackEndMs: number,
  events: TimelineSectionEvent[],
  anchorMs: number,
  trackWidthPx: number,
  slotWidthPx: number,
): BandSegment[] {
  const raw = buildBandSegments(trackStartMs, trackEndMs, events, anchorMs, trackWidthPx, slotWidthPx);
  return mergeAdjacentSameBarSegments(raw);
}

function computeThumbLayout(
  scrollLeft: number,
  scrollWidth: number,
  clientWidth: number,
  trackInnerWidth: number,
): { thumbLeftPx: number; thumbWidthPx: number; maxScroll: number } {
  const maxScroll = Math.max(0, scrollWidth - clientWidth);
  if (maxScroll <= 0) {
    const safeTrackW = Math.max(0, trackInnerWidth);
    return { thumbLeftPx: 0, thumbWidthPx: safeTrackW, maxScroll: 0 };
  }
  /** When the thumb track has not been measured yet (0), use viewport width so the thumb can render and RO can update. */
  const safeTrackW =
    trackInnerWidth > 0 ? trackInnerWidth : Math.max(SCROLL_THUMB_MIN_WIDTH_PX * 3, clientWidth);
  const ratioViewport = Math.min(1, clientWidth / scrollWidth);
  const thumbWidthPx = Math.max(
    SCROLL_THUMB_MIN_WIDTH_PX,
    Math.min(SCROLL_THUMB_MAX_WIDTH_PX, ratioViewport * safeTrackW),
  );
  const thumbTravel = Math.max(0, safeTrackW - thumbWidthPx);
  const thumbLeftPx = (scrollLeft / maxScroll) * thumbTravel;
  return { thumbLeftPx, thumbWidthPx, maxScroll };
}

export function TimelineSection({
  title,
  subtitle,
  windowStart,
  windowEnd,
  events,
  eventLabels,
  annotationCopy,
  sectionHeaderVariant = 'vitalsPanel',
  timelineRegionAriaLabel,
  className,
}: TimelineSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const thumbTrackRef = useRef<HTMLDivElement>(null);
  const thumbDragRef = useRef<{
    pointerId: number;
    startClientX: number;
    startScrollLeft: number;
    maxScroll: number;
    thumbTravel: number;
  } | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [trackInnerW, setTrackInnerW] = useState(0);
  const [scrollMetrics, setScrollMetrics] = useState({ left: 0, sw: 0, cw: 0 });
  const detailRef = useRef<HTMLDivElement>(null);
  const [measuredAnnotationWidthsPx, setMeasuredAnnotationWidthsPx] = useState<Record<string, number>>({});
  const [timelineHover, setTimelineHover] = useState<TimelineHoverState>(null);
  const hoverClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** After first layout with overflow, pin horizontal scroll to the right (current time). */
  const pendingScrollToEndRef = useRef(true);

  const cancelHoverClearTimer = useCallback(() => {
    if (hoverClearTimerRef.current) {
      clearTimeout(hoverClearTimerRef.current);
      hoverClearTimerRef.current = null;
    }
  }, []);

  const handleEventHoverEnter = useCallback((eventId: string) => {
    cancelHoverClearTimer();
    setTimelineHover({ mode: 'event', eventId });
  }, [cancelHoverClearTimer]);

  const handleBandHoverEnter = useCallback((segment: BandSegment) => {
    cancelHoverClearTimer();
    setTimelineHover({
      mode: 'band',
      segmentId: segment.id,
    });
  }, [cancelHoverClearTimer]);

  const handleTimelineHoverLeave = useCallback(() => {
    if (hoverClearTimerRef.current) {
      clearTimeout(hoverClearTimerRef.current);
    }
    hoverClearTimerRef.current = setTimeout(() => {
      setTimelineHover(null);
      hoverClearTimerRef.current = null;
    }, EVENT_HOVER_LEAVE_DELAY_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverClearTimerRef.current) {
        clearTimeout(hoverClearTimerRef.current);
      }
    };
  }, []);

  const syncScrollMetrics = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollMetrics({
      left: el.scrollLeft,
      sw: el.scrollWidth,
      cw: el.clientWidth,
    });
  }, []);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }
    let scrollRafId = 0;
    const measure = () => {
      setContainerWidth(el.clientWidth);
      setScrollMetrics({
        left: el.scrollLeft,
        sw: el.scrollWidth,
        cw: el.clientWidth,
      });
    };
    const scheduleScrollMetrics = () => {
      if (scrollRafId !== 0) return;
      scrollRafId = window.requestAnimationFrame(() => {
        scrollRafId = 0;
        setScrollMetrics({
          left: el.scrollLeft,
          sw: el.scrollWidth,
          cw: el.clientWidth,
        });
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    el.addEventListener('scroll', scheduleScrollMetrics, { passive: true });
    return () => {
      if (scrollRafId !== 0) {
        window.cancelAnimationFrame(scrollRafId);
      }
      ro.disconnect();
      el.removeEventListener('scroll', scheduleScrollMetrics);
    };
  }, []);

  const ticks = useMemo(() => buildTicks(windowStart, windowEnd), [windowStart, windowEnd]);
  const anchorMs = ticks[0]?.getTime() ?? windowStart.getTime();
  const trackEndMs = windowEnd.getTime();
  /** Visible span in time — may be a non-integer number of slot quanta so the track ends at real `windowEnd`. */
  const spanMs = Math.max(SLOT_MS, trackEndMs - anchorMs);
  const naturalWidthPx = Math.max(SLOT_WIDTH_PX, (spanMs / SLOT_MS) * SLOT_WIDTH_PX);
  const trackWidthPx = Math.max(naturalWidthPx, containerWidth);
  /** Pixels per slot quantum so `timeToPx` maps [anchorMs, trackEndMs] → [0, trackWidthPx]. */
  const slotWidthPx = (trackWidthPx * SLOT_MS) / spanMs;

  const bandSegments = useMemo(
    () => buildBandSegmentsMerged(anchorMs, trackEndMs, events, anchorMs, trackWidthPx, slotWidthPx),
    [anchorMs, trackEndMs, events, trackWidthPx, slotWidthPx],
  );

  const eventBandSegmentIdByEventId = useMemo(() => {
    const map = new Map<string, string>();
    for (const ev of events) {
      const segId = resolveEventBandSegmentId(ev, bandSegments);
      if (segId) {
        map.set(ev.id, segId);
      }
    }
    return map;
  }, [events, bandSegments]);

  const basePillLayouts = useMemo(() => {
    return events.map(ev => {
      const rawLeft = timeToPx(ev.start.getTime(), anchorMs, slotWidthPx);
      const rawRight = timeToPx(ev.end.getTime(), anchorMs, slotWidthPx);
      const leftPx = Math.max(0, rawLeft);
      const rightPx = Math.min(trackWidthPx, rawRight);
      const widthPx = Math.max(4, rightPx - leftPx);
      const { connectorColor, detailColor } = segmentBarColors(
        ev.kind === 'service_restored' ? 'service_restored' : ev.kind,
      );
      const label = labelForKind(ev.kind, eventLabels);
      const duration = formatDuration(ev.start, ev.end, annotationCopy);
      const detailLine =
        ev.kind === 'service_restored'
          ? replacePlaceholders(annotationCopy.eventSubtitleLabelOnly, { label })
          : replacePlaceholders(annotationCopy.eventSubtitle, { label, duration });
      const anchorMsEv = annotationAnchorMs(ev);
      const connectorLeftPx = timeToPx(anchorMsEv, anchorMs, slotWidthPx);
      const timeLine = format(new Date(anchorMsEv), 'h:mm a');
      return {
        id: ev.id,
        kind: ev.kind,
        anchorMs: anchorMsEv,
        leftPx,
        widthPx,
        connectorColor,
        detailColor,
        connectorLeftPx,
        timeLine,
        detailLine,
      };
    });
  }, [events, eventLabels, annotationCopy, anchorMs, trackWidthPx, slotWidthPx]);

  useLayoutEffect(() => {
    const root = detailRef.current;
    if (!root) return;
    const next: Record<string, number> = {};
    root.querySelectorAll<HTMLElement>('[data-timeline-annotation-id]').forEach(node => {
      const id = node.getAttribute('data-timeline-annotation-id');
      if (id) {
        next[id] = Math.round(node.getBoundingClientRect().width * 100) / 100;
      }
    });
    setMeasuredAnnotationWidthsPx(prev => (sameAnnotationWidthMap(prev, next) ? prev : next));
  }, [basePillLayouts, measuredAnnotationWidthsPx]);

  const pillLayouts = useMemo(() => {
    const preferredSideById = computeAnnotationSidesByOrder(basePillLayouts);
    const placementById = computeAnnotationPlacementsByOverlap(
      basePillLayouts.map((p) => ({
        ...p,
        preferredSide: preferredSideById[p.id] ?? 'above',
      })),
      measuredAnnotationWidthsPx,
      trackWidthPx,
    );
    return basePillLayouts.map(p => {
      const placement = placementById[p.id];
      const annotationSide = placement?.side ?? preferredSideById[p.id] ?? 'above';
      const connectorTier = placement?.connectorTier ?? 0;
      const labelWidthPx = annotationOverlapLabelWidthPx(p, measuredAnnotationWidthsPx);
      const annotationAnchor = computeAnnotationHorizontalAnchor(
        p.connectorLeftPx,
        labelWidthPx,
        trackWidthPx,
      );
      const columnBox =
        placement != null
          ? {
              topPx: placement.connectorColumnTopPx,
              heightPx: placement.connectorColumnHeightPx,
            }
          : connectorColumnBox(annotationSide, connectorTier);
      return {
        ...p,
        bandSegmentId: eventBandSegmentIdByEventId.get(p.id),
        annotationSide,
        connectorTier,
        connectorColumnTopPx: columnBox.topPx,
        connectorColumnHeightPx: columnBox.heightPx,
        annotationTopPx: placement?.labelTopPx ?? annotationLabelTopPx(annotationSide, connectorTier),
        annotationAnchor,
      };
    });
  }, [basePillLayouts, eventBandSegmentIdByEventId, measuredAnnotationWidthsPx, trackWidthPx]);

  const connectorLinePositionsPx = useMemo(
    () => new Set(pillLayouts.map(p => snapTimelineLineLeftPx(p.connectorLeftPx))),
    [pillLayouts],
  );

  useLayoutEffect(() => {
    const el = thumbTrackRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }
    const m = () => {
      setTrackInnerW(el.clientWidth);
    };
    m();
    const ro = new ResizeObserver(m);
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [containerWidth, trackWidthPx, ticks.length]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !pendingScrollToEndRef.current) return;
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    if (maxScroll <= 0) return;
    el.scrollLeft = maxScroll;
    pendingScrollToEndRef.current = false;
    setScrollMetrics({
      left: el.scrollLeft,
      sw: el.scrollWidth,
      cw: el.clientWidth,
    });
  }, [trackWidthPx, containerWidth, ticks.length]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollMetrics({
      left: el.scrollLeft,
      sw: el.scrollWidth,
      cw: el.clientWidth,
    });
  }, [trackWidthPx, ticks.length, containerWidth]);

  const timelineCopy = i18n.monitors.detail.timelineSection;

  const thumbLayout = useMemo(
    () => computeThumbLayout(scrollMetrics.left, scrollMetrics.sw, scrollMetrics.cw, trackInnerW),
    [scrollMetrics.left, scrollMetrics.sw, scrollMetrics.cw, trackInnerW],
  );

  const canScrollLeft = thumbLayout.maxScroll > 0 && scrollMetrics.left > 1;
  const canScrollRight = thumbLayout.maxScroll > 0 && scrollMetrics.left < thumbLayout.maxScroll - 1;

  const scrollTimelineBy = useCallback((delta: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: scrollBehaviorForProgrammaticScroll() });
  }, []);

  const onScrollEarlier = useCallback(() => {
    scrollTimelineBy(-SLOT_WIDTH_PX);
  }, [scrollTimelineBy]);

  const onScrollLater = useCallback(() => {
    scrollTimelineBy(SLOT_WIDTH_PX);
  }, [scrollTimelineBy]);

  const onThumbTrackPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).dataset?.thumb === 'true') return;
    const pan = scrollRef.current;
    const track = thumbTrackRef.current;
    if (!pan || !track) return;
    const { thumbWidthPx, maxScroll } = computeThumbLayout(
      pan.scrollLeft,
      pan.scrollWidth,
      pan.clientWidth,
      track.clientWidth,
    );
    if (maxScroll <= 0) return;
    const rect = track.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const trackW = track.clientWidth;
    const thumbTravel = Math.max(0, trackW - thumbWidthPx);
    const ratio = thumbTravel > 0 ? Math.min(1, Math.max(0, (x - thumbWidthPx / 2) / thumbTravel)) : 0;
    pan.scrollTo({ left: ratio * maxScroll, behavior: scrollBehaviorForProgrammaticScroll() });
  }, []);

  const onThumbPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const pan = scrollRef.current;
    const track = thumbTrackRef.current;
    if (!pan || !track) return;
    const { thumbWidthPx, maxScroll } = computeThumbLayout(
      pan.scrollLeft,
      pan.scrollWidth,
      pan.clientWidth,
      track.clientWidth,
    );
    if (maxScroll <= 0) return;
    const trackW = track.clientWidth;
    const thumbTravel = Math.max(0, trackW - thumbWidthPx);
    thumbDragRef.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startScrollLeft: pan.scrollLeft,
      maxScroll,
      thumbTravel,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onThumbPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = thumbDragRef.current;
    const pan = scrollRef.current;
    if (!drag || e.pointerId !== drag.pointerId || !pan) return;
    if (drag.thumbTravel <= 0) return;
    const dx = e.clientX - drag.startClientX;
    const dScroll = (dx / drag.thumbTravel) * drag.maxScroll;
    pan.scrollLeft = Math.min(drag.maxScroll, Math.max(0, drag.startScrollLeft + dScroll));
  }, []);

  const endThumbDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = thumbDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    thumbDragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <TimelineSectionOuter className={className}>
      <Root>
        <SectionHeaderSlot>
          <SectionHeader title={title} subtitle={subtitle} variant={sectionHeaderVariant} />
        </SectionHeaderSlot>
        <TimelinePan ref={scrollRef} role="region" aria-label={timelineRegionAriaLabel}>
          <Track $widthPx={trackWidthPx}>
            <Belt>
              {ticks.map((tick, i) => {
                const startMs = tick.getTime();
                const endMs = i + 1 < ticks.length ? ticks[i + 1]!.getTime() : trackEndMs;
                const flexGrow = Math.max(1, endMs - startMs);
                return (
                  <TickCell
                    key={`${tick.getTime()}-${i}`}
                    style={{ flex: `${flexGrow} 1 0%`, minWidth: 0 }}
                  >
                    <TickLabel>{format(tick, 'h:mm a')}</TickLabel>
                  </TickCell>
                );
              })}
            </Belt>
            <Detail ref={detailRef}>
              <GuidesLayer aria-hidden>
                {ticks.map((tick, i) => {
                  const snappedLeftPx = snapTimelineLineLeftPx(timeToPx(tick.getTime(), anchorMs, slotWidthPx));
                  if (connectorLinePositionsPx.has(snappedLeftPx)) {
                    return null;
                  }
                  return <GuideLine key={`g-${i}-${tick.getTime()}`} $leftPx={snappedLeftPx} />;
                })}
              </GuidesLayer>
              <BarsLayer aria-hidden>
                {bandSegments.map(seg => {
                  const { bar } = segmentBarColors(seg.kind);
                  const isBandDimmed = isBandSegmentDimmed(
                    timelineHover,
                    seg.id,
                    eventBandSegmentIdByEventId,
                  );
                  return (
                    <React.Fragment key={seg.id}>
                      <DurationBar
                        $leftPx={seg.leftPx}
                        $widthPx={seg.widthPx}
                        $bg={bar}
                        $dimmed={isBandDimmed}
                      />
                      <DurationBarHitTarget
                        $leftPx={seg.leftPx}
                        $widthPx={seg.widthPx}
                        onMouseEnter={() => handleBandHoverEnter(seg)}
                        onMouseLeave={handleTimelineHoverLeave}
                      />
                    </React.Fragment>
                  );
                })}
              </BarsLayer>
              <ConnectorsLayer>
                {pillLayouts.map(p => {
                  const isHighlighted = isTimelineEventHighlighted(
                    timelineHover,
                    p.id,
                    p.bandSegmentId,
                  );
                  const isDimmed = isTimelineEventDimmed(timelineHover, p.id, p.bandSegmentId);
                  return (
                  <EventConnectorColumn
                    key={`conn-${p.id}`}
                    $leftPx={snapTimelineLineLeftPx(p.connectorLeftPx)}
                    $topPx={p.connectorColumnTopPx}
                    $heightPx={p.connectorColumnHeightPx}
                    $dimmed={isDimmed}
                    $highlighted={isHighlighted}
                  >
                    {p.annotationSide === 'above' ? (
                      <>
                        <EventConnectorDot $color={p.connectorColor} $side="above" />
                        <EventConnectorLineAbove $lineColor={p.connectorColor} />
                      </>
                    ) : (
                      <>
                        <EventConnectorLineBelow $lineColor={p.connectorColor} />
                        <EventConnectorDot $color={p.connectorColor} $side="below" />
                      </>
                    )}
                  </EventConnectorColumn>
                  );
                })}
              </ConnectorsLayer>
              <AnnotationsLayer>
                {pillLayouts.map(p => {
                  const isHighlighted = isTimelineEventHighlighted(
                    timelineHover,
                    p.id,
                    p.bandSegmentId,
                  );
                  const isDimmed = isTimelineEventDimmed(timelineHover, p.id, p.bandSegmentId);
                  const labelWidthPx = annotationOverlapLabelWidthPx(p, measuredAnnotationWidthsPx);
                  return (
                  <React.Fragment key={`event-${p.id}`}>
                    <EventConnectorHitTarget
                      $leftPx={p.connectorLeftPx}
                      $topPx={p.connectorColumnTopPx}
                      $heightPx={p.connectorColumnHeightPx}
                      onMouseEnter={() => handleEventHoverEnter(p.id)}
                      onMouseLeave={handleTimelineHoverLeave}
                    />
                    <EventAnnotationHitTarget
                      $leftPx={p.connectorLeftPx}
                      $topPx={p.annotationTopPx}
                      $widthPx={labelWidthPx}
                      $heightPx={ANNOTATION_LABEL_BLOCK_HEIGHT_PX}
                      $anchor={p.annotationAnchor}
                      onMouseEnter={() => handleEventHoverEnter(p.id)}
                      onMouseLeave={handleTimelineHoverLeave}
                    />
                  <AnnotationBlock
                    data-timeline-annotation-id={p.id}
                    $leftPx={p.connectorLeftPx}
                    $topPx={p.annotationTopPx}
                    $anchor={p.annotationAnchor}
                    $dimmed={isDimmed}
                    $highlighted={isHighlighted}
                  >
                    <AnnotationText $anchor={p.annotationAnchor}>
                      <AnnotationTimeLine>{p.timeLine}</AnnotationTimeLine>
                      <AnnotationDetailLine $color={p.detailColor}>{p.detailLine}</AnnotationDetailLine>
                    </AnnotationText>
                  </AnnotationBlock>
                  </React.Fragment>
                  );
                })}
              </AnnotationsLayer>
            </Detail>
          </Track>
        </TimelinePan>
      </Root>
      <ScrollbarRail>
          <ScrollArrowButton
            type="button"
            disabled={!canScrollLeft}
            aria-label={timelineCopy.scrollEarlierAria}
            onClick={onScrollEarlier}
          >
            <TimelineScrollbarChevronIcon
              side="left"
              glyphFill={canScrollLeft ? panels.subtitleMuted : panels.scrollbarThumb}
            />
          </ScrollArrowButton>
          <ThumbTrack ref={thumbTrackRef} onPointerDown={onThumbTrackPointerDown}>
            {thumbLayout.maxScroll > 0 ? (
              <ScrollThumb
                data-thumb="true"
                $leftPx={thumbLayout.thumbLeftPx}
                $widthPx={thumbLayout.thumbWidthPx}
                onPointerDown={onThumbPointerDown}
                onPointerMove={onThumbPointerMove}
                onPointerUp={endThumbDrag}
                onPointerCancel={endThumbDrag}
              />
            ) : null}
          </ThumbTrack>
          <ScrollArrowButton
            type="button"
            disabled={!canScrollRight}
            aria-label={timelineCopy.scrollLaterAria}
            onClick={onScrollLater}
          >
            <TimelineScrollbarChevronIcon
              side="right"
              glyphFill={canScrollRight ? panels.subtitleMuted : panels.scrollbarThumb}
            />
          </ScrollArrowButton>
      </ScrollbarRail>
    </TimelineSectionOuter>
  );
}
